/**
 * TaskAnalyzer — 쓰레드 대화를 태스크 카드 데이터로 변환
 *
 * AiMemoAnalyzer 패턴 참고.
 * Firebase AI Logic SDK (Gemini 2.5 Flash-Lite) 사용.
 */

import { getAiTextModel } from './AiTextClient';
import { getApp } from 'firebase/app';

// ---------------------------------------------------------------------------
// Gemini Model Setup
// ---------------------------------------------------------------------------

const MODEL_NAME = 'gemini-2.5-flash-lite';

// 서버 경유 — 브라우저가 Firebase AI Logic 을 직접 부르면 공개된 클라이언트 설정만으로
// 외부인이 우리 프로젝트 명의로 모델을 호출할 수 있다 (2026-08-10 비용 사고). 계약은 동일.
function getModel() {
  return getAiTextModel(MODEL_NAME);
}

// ---------------------------------------------------------------------------
// JSON 파싱 헬퍼 (AiMemoAnalyzer 재사용)
// ---------------------------------------------------------------------------

function parseJSON(text: string): any {
  const cleaned = text
    .replace(/^```json?\n?/g, '')
    .replace(/\n?```$/g, '')
    .trim();
  return JSON.parse(cleaned);
}

function langLabel(lang: string): string {
  if (lang.startsWith('ko')) return 'Korean';
  if (lang.startsWith('ja')) return 'Japanese';
  return 'English';
}

// ---------------------------------------------------------------------------
// 타입 정의
// ---------------------------------------------------------------------------

export interface TaskAnalysisResult {
  title: string;
  summary: string;
  suggestedAssignee: string | null;     // 멘션된 사용자 이름 (표시명)
  suggestedDueDate: string | null;      // ISO 8601 날짜 (AI가 추정)
  tags: string[];
  actionItems: string[];                 // 핵심 액션 목록
}

// ---------------------------------------------------------------------------
// analyzeThread — 쓰레드 대화 분석 → 태스크 카드 데이터
// ---------------------------------------------------------------------------

/**
 * 쓰레드 대화 텍스트를 AI로 분석하여 태스크 카드 생성에 필요한 데이터를 추출합니다.
 *
 * @param threadText - UtilApp.getThreadText()로 추출한 쓰레드 전체 텍스트
 * @param language - 사용자 언어 (ko, ja, en)
 */
export async function analyzeThread(
  threadText: string,
  language?: string,
): Promise<TaskAnalysisResult> {
  const lang = language || 'ko';
  const model = getModel();

  const prompt = `You are an AI assistant that analyzes chat thread conversations and extracts actionable tasks.

**Thread conversation:**
${threadText}

**Instructions:**
1. Extract a short, clear title that summarizes the main task or discussion topic.
2. Write a brief summary (2-3 sentences) of what was discussed and what needs to be done.
3. Identify who seems to be the most likely assignee (the person who should take action). Return their display name, or null if unclear.
4. If any deadline or due date was mentioned or implied, extract it in ISO 8601 format (YYYY-MM-DD). Return null if no deadline was mentioned.
5. Generate 3-5 relevant tags for categorization.
6. List specific action items (things that need to be done).

**Response language:** ${langLabel(lang)}

**IMPORTANT:** Return ONLY valid JSON, no markdown code blocks. Use this exact structure:
{
  "title": "concise task title",
  "summary": "brief summary of discussion and required action",
  "suggestedAssignee": "person name or null",
  "suggestedDueDate": "YYYY-MM-DD or null",
  "tags": ["tag1", "tag2", "tag3"],
  "actionItems": ["action 1", "action 2"]
}`;

  const result = await model.generateContent(prompt);
  const text = result.response.text();
  const parsed = parseJSON(text);

  return {
    title: parsed.title || '',
    summary: parsed.summary || '',
    suggestedAssignee: parsed.suggestedAssignee || null,
    suggestedDueDate: parsed.suggestedDueDate || null,
    tags: (parsed.tags || []).slice(0, 5),
    actionItems: parsed.actionItems || [],
  };
}

// ---------------------------------------------------------------------------
// Records 분석 결과 타입
// ---------------------------------------------------------------------------

export interface RecordAnalysisResult {
  title: string;                  // Records 제목 (원본 대화의 핵심 주제)
  summary: string;                // 전체 흐름 요약 (원본 대화 + 진행 과정 + 결론)
  suggestedCategory: string;      // 추천 카테고리
  tags: string[];                 // 태그 3~5개
  keyDecisions: string[];         // 핵심 결정사항
  outcome: string;                // 최종 결과/성과
}

// ---------------------------------------------------------------------------
// analyzeForRecord — 완료된 태스크를 Records용으로 분석
// ---------------------------------------------------------------------------

/**
 * 태스크 카드의 전체 타임라인(원본 쓰레드 + 카드 댓글)을 분석하여
 * Records 기록용 요약 데이터를 생성합니다.
 *
 * @param threadText - 원본 쓰레드 텍스트
 * @param commentTexts - 태스크 카드 댓글 배열 (작성자: 내용 형식)
 * @param cardTitle - 태스크 카드 제목
 * @param existingCategories - 기존 카테고리 목록 (추천 시 참고)
 * @param language - 사용자 언어
 */
export async function analyzeForRecord(
  threadText: string,
  commentTexts: string[],
  cardTitle: string,
  existingCategories: string[],
  language?: string,
): Promise<RecordAnalysisResult> {
  const lang = language || 'ko';
  const model = getModel();

  const categoryContext = existingCategories.length > 0
    ? `\n**Existing categories (prefer matching one of these):** ${existingCategories.join(', ')}`
    : '';

  const commentsBlock = commentTexts.length > 0
    ? `\n**Follow-up comments on the task card:**\n${commentTexts.join('\n')}`
    : '';

  const prompt = `You are an AI assistant that creates archival summaries for completed work tasks.

**Original task card title:** ${cardTitle}

**Original thread conversation:**
${threadText}
${commentsBlock}
${categoryContext}

**Instructions:**
1. Create a clear, descriptive title for this archived record.
2. Write a comprehensive summary (3-5 sentences) covering: what was discussed, what actions were taken, and what the outcome was.
3. Suggest ONE category that best describes this work item. If existing categories are provided, prefer matching one of them. If none fit, suggest a new short category name.
4. Generate 3-5 relevant tags for searchability.
5. List 2-4 key decisions that were made during this process.
6. Summarize the final outcome or result in one sentence.

**Response language:** ${langLabel(lang)}

**IMPORTANT:** Return ONLY valid JSON, no markdown code blocks. Use this exact structure:
{
  "title": "archive record title",
  "summary": "comprehensive summary of the entire process",
  "suggestedCategory": "category name",
  "tags": ["tag1", "tag2", "tag3"],
  "keyDecisions": ["decision 1", "decision 2"],
  "outcome": "one sentence describing the final result"
}`;

  const result = await model.generateContent(prompt);
  const text = result.response.text();
  const parsed = parseJSON(text);

  return {
    title: parsed.title || cardTitle,
    summary: parsed.summary || '',
    suggestedCategory: parsed.suggestedCategory || '',
    tags: (parsed.tags || []).slice(0, 5),
    keyDecisions: parsed.keyDecisions || [],
    outcome: parsed.outcome || '',
  };
}
